import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Activity, ArrowLeft, Radio, Mic, Users, AlertTriangle, Wifi, WifiOff,
  Clock, FlaskConical, Volume2, MessageSquareText, Server, Languages, Loader2,
  VolumeX, Trash2, Eraser, PlayCircle, ShieldAlert, ListChecks,
  Smartphone, RefreshCw, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const LANG_NAMES = { pt: 'Português', en: 'English', es: 'Español', fr: 'Français', de: 'Deutsch', it: 'Italiano' };

const STATE_CFG = {
  normal: { label: 'NORMAL', badge: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40', dot: 'bg-emerald-400' },
  degraded: { label: 'DEGRADADO', badge: 'bg-amber-500/15 text-amber-400 border-amber-500/40', dot: 'bg-amber-400' },
  critical: { label: 'CRÍTICO', badge: 'bg-red-500/15 text-red-400 border-red-500/40 animate-pulse', dot: 'bg-red-500' },
  suspended: { label: 'SUSPENSO', badge: 'bg-slate-500/15 text-slate-300 border-slate-500/40', dot: 'bg-slate-400' },
  inactive: { label: 'INATIVO', badge: 'bg-slate-500/15 text-slate-400 border-slate-600/40', dot: 'bg-slate-500' },
  waiting: { label: 'EM ESPERA', badge: 'bg-slate-500/15 text-slate-400 border-slate-600/40', dot: 'bg-slate-500' },
};

const TREND_CFG = {
  improving: { label: 'a melhorar', cls: 'text-emerald-400', arrow: '▼' },
  stable: { label: 'estável', cls: 'text-slate-500', arrow: '–' },
  worsening: { label: 'a piorar', cls: 'text-red-400', arrow: '▲' },
};

const fmtS = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(1)}s`);
const fmtAgo = (v) => (v === null || v === undefined ? '—' : v < 2 ? 'agora' : `há ${Math.round(v)}s`);
const fmtPct = (v) => (v === null || v === undefined ? '—' : `${v}%`);

const ConfirmModal = ({ confirm, onCancel, onConfirm }) => {
  const [step, setStep] = useState(1);
  useEffect(() => { setStep(1); }, [confirm]);
  if (!confirm) return null;
  const needsSecond = confirm.double && step === 1;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="confirm-modal">
      <div className={`w-full max-w-md rounded-xl border bg-slate-900 p-5 space-y-4 ${confirm.danger ? 'border-red-500/50' : 'border-slate-700'}`}>
        <div className="flex items-center gap-2">
          <ShieldAlert className={`w-5 h-5 ${confirm.danger ? 'text-red-400' : 'text-amber-400'}`} />
          <h3 className="font-['Manrope'] font-bold text-white text-sm">{confirm.title}</h3>
        </div>
        <p className="text-xs text-slate-400 leading-relaxed">{confirm.message}</p>
        {confirm.double && step === 2 && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-[11px] text-red-300" data-testid="confirm-step2-warning">
            Confirmação final: esta ação é imediata e ficará registada em auditoria. Tem a certeza?
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} data-testid="confirm-cancel-btn"
            className="text-xs px-3 py-1.5 rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800">
            Cancelar
          </button>
          {needsSecond ? (
            <button onClick={() => setStep(2)} data-testid="confirm-step1-btn"
              className="text-xs px-3 py-1.5 rounded-md bg-amber-600/80 hover:bg-amber-600 text-white font-semibold">
              Continuar
            </button>
          ) : (
            <button onClick={onConfirm} data-testid="confirm-step2-btn"
              className={`text-xs px-3 py-1.5 rounded-md text-white font-semibold ${confirm.danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
              {confirm.double ? 'Confirmar definitivamente' : 'Confirmar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const AckPanel = ({ lang, acks, samplingActive }) => {
  if (!acks) return null;
  const r = acks.rates || {};
  const sampleTxt = `amostra ${acks.sample_size}/${acks.eligible} participantes`;
  const rows = [
    ['Legendas recebidas', r.subtitle_received],
    ['Legendas renderizadas', r.subtitle_rendered],
    ['Áudio recebido', r.audio_received],
    ['Reprodução iniciada', r.playback_started],
    ['Reprodução concluída', r.playback_completion],
  ];
  return (
    <div className="pt-2 border-t border-slate-800 space-y-1" data-testid={`ack-panel-${lang}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
        <ListChecks className="w-3 h-3" /> ACKs ponta-a-ponta
        <span className="normal-case text-slate-600">· {samplingActive ? sampleTxt : `${acks.sample_size} participante(s), ACK completo`}</span>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
        {rows.map(([label, val]) => (
          <div key={label} className="flex items-center justify-between text-[10px]">
            <span className="text-slate-500">{label}</span>
            <span className={`font-semibold ${val === null || val === undefined ? 'text-slate-600' : val >= 90 ? 'text-emerald-400' : val >= 70 ? 'text-amber-400' : 'text-red-400'}`}>{fmtPct(val)}</span>
          </div>
        ))}
        <div className="flex items-center justify-between text-[10px]">
          <span className="text-slate-500">t. conf. receção p95</span>
          <span className="text-slate-400 font-semibold">{fmtS((acks.latency?.receive || {}).p95)}</span>
        </div>
        <div className="flex items-center justify-between text-[10px]">
          <span className="text-slate-500">t. conf. renderização p95</span>
          <span className="text-slate-400 font-semibold">{fmtS((acks.latency?.render || {}).p95)}</span>
        </div>
        <div className="flex items-center justify-between text-[10px]">
          <span className="text-slate-500">t. conf. início reprod. p95</span>
          <span className="text-slate-400 font-semibold">{fmtS((acks.latency?.playback_start || {}).p95)}</span>
        </div>
        <div className="flex items-center justify-between text-[10px]">
          <span className="text-slate-500">t. conf. conclusão p95</span>
          <span className="text-slate-400 font-semibold">{fmtS((acks.latency?.playback_complete || {}).p95)}</span>
        </div>
      </div>
      <p className="text-[9px] text-slate-600">Tempos medidos no servidor (emissão → receção do ACK): incluem ida + retorno, não são medição unidirecional no dispositivo. Ausência de ACK não significa falha.</p>
    </div>
  );
};

const StateBadge = ({ state, small, testId }) => {
  const cfg = STATE_CFG[state] || STATE_CFG.waiting;
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full border font-['Manrope'] font-bold tracking-wider ${cfg.badge} ${small ? 'text-[10px] px-2 py-0.5' : 'text-xs px-3 py-1'}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

const Trend = ({ trend, testId }) => {
  const cfg = TREND_CFG[trend] || TREND_CFG.stable;
  return (
    <span data-testid={testId} className={`text-[10px] font-semibold ${cfg.cls}`} title={`Tendência: ${cfg.label}`}>
      {cfg.arrow} {cfg.label}
    </span>
  );
};

const Metric = ({ label, value, sub, trend, testId }) => (
  <div className="flex flex-col" data-testid={testId}>
    <span className="text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
    <span className="font-['Manrope'] text-lg font-bold text-slate-100 leading-tight">{value}</span>
    <span className="flex items-center gap-2">
      {sub && <span className="text-[10px] text-slate-500">{sub}</span>}
      {trend && <Trend trend={trend} />}
    </span>
  </div>
);

const LangCard = ({ lang, data, acks, samplingActive, onAction, busy }) => {
  const name = LANG_NAMES[lang] || lang;
  return (
    <div
      data-testid={`lang-card-${lang}`}
      className={`rounded-xl border bg-slate-900/70 p-4 space-y-3 ${
        data.state === 'critical' ? 'border-red-500/50' : data.state === 'degraded' ? 'border-amber-500/40' : 'border-slate-800'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Languages className="w-4 h-4 text-blue-400" />
          <span className="font-['Manrope'] font-bold text-white uppercase">{lang}</span>
          <span className="text-xs text-slate-500">{name}</span>
          {data.tts_suspended && (
            <span className="inline-flex items-center gap-1 rounded-full border border-orange-500/50 bg-orange-500/15 text-orange-300 text-[9px] font-bold px-2 py-0.5 tracking-wider" data-testid={`lang-tts-suspended-${lang}`}>
              <VolumeX className="w-2.5 h-2.5" /> TTS SUSPENSO
            </span>
          )}
        </div>
        <StateBadge state={data.state} small testId={`lang-state-${lang}`} />
      </div>

      {data.reasons && data.reasons.length > 0 && (
        <div className="text-[10px] text-amber-400/90" data-testid={`lang-reasons-${lang}`}>
          {data.reasons.join(' · ')}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Metric
          label="Tradução (60s)"
          value={fmtS(data.translation.avg_60s)}
          sub={`p95 ${fmtS(data.translation.p95_60s)} · ${data.translation.count_60s} seg`}
          trend={data.translation.trend}
          testId={`lang-translation-${lang}`}
        />
        <Metric
          label="TTS (60s)"
          value={fmtS(data.tts.avg_60s)}
          sub={`p95 ${fmtS(data.tts.p95_60s)}`}
          trend={data.tts.trend}
          testId={`lang-tts-${lang}`}
        />
        <Metric
          label="Fila TTS"
          value={String(data.tts.queue_depth)}
          trend={data.tts.queue_trend}
          testId={`lang-queue-${lang}`}
        />
        <Metric
          label="Participantes"
          value={String(data.delivery.participants)}
          sub={`${data.delivery.subtitles_sent} leg · ${data.delivery.audio_sent} áudio`}
          testId={`lang-participants-${lang}`}
        />
        <Metric
          label="Lat. legendas"
          value={fmtS(data.subtitle_latency.p50)}
          sub={`p95 ${fmtS(data.subtitle_latency.p95)}`}
          trend={data.subtitle_latency.trend}
          testId={`lang-sublat-${lang}`}
        />
        <Metric
          label="Lat. áudio"
          value={fmtS(data.audio_latency.p50)}
          sub={`p95 ${fmtS(data.audio_latency.p95)}`}
          trend={data.audio_latency.trend}
          testId={`lang-audlat-${lang}`}
        />
      </div>

      <div className="flex items-center justify-between text-[10px] text-slate-500 pt-1 border-t border-slate-800">
        <span>Última legenda: {fmtAgo(data.delivery.last_subtitle_ago)}</span>
        <span>Último áudio: {fmtAgo(data.delivery.last_audio_ago)}</span>
        {(data.translation.errors_5m + data.tts.errors_5m) > 0 && (
          <span className="text-red-400">{data.translation.errors_5m + data.tts.errors_5m} erro(s)/5min</span>
        )}
      </div>

      <AckPanel lang={lang} acks={acks} samplingActive={samplingActive} />

      {onAction && (
        <div className="flex flex-wrap gap-1.5 pt-2 border-t border-slate-800">
          {data.tts_suspended ? (
            <button disabled={busy} onClick={() => onAction('tts_resume', lang)} data-testid={`lang-resume-tts-${lang}`}
              className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md border border-emerald-600/50 text-emerald-300 hover:bg-emerald-600/15 disabled:opacity-40">
              <Volume2 className="w-3 h-3" /> Retomar TTS
            </button>
          ) : (
            <button disabled={busy} onClick={() => onAction('tts_suspend', lang)} data-testid={`lang-suspend-tts-${lang}`}
              className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md border border-orange-600/50 text-orange-300 hover:bg-orange-600/15 disabled:opacity-40">
              <VolumeX className="w-3 h-3" /> Suspender TTS
            </button>
          )}
          <button disabled={busy} onClick={() => onAction('tts_clear_queue', lang)} data-testid={`lang-clear-queue-${lang}`}
            className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md border border-red-600/50 text-red-300 hover:bg-red-600/15 disabled:opacity-40">
            <Trash2 className="w-3 h-3" /> Limpar fila ({data.tts.queue_depth})
          </button>
          {data.tts_suspended && (
            <span className="text-[9px] text-orange-300/80 w-full" data-testid={`suspend-queue-info-${lang}`}>
              Fila reproduzível: {data.tts.queue_depth} seg. · descartados durante a suspensão: {data.suspend_discarded || 0}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default function EventMode() {
  const { sessionId } = useParams();
  const [snap, setSnap] = useState(null);
  const [wsStatus, setWsStatus] = useState('connecting');
  const [authError, setAuthError] = useState(null);
  const [elapsed, setElapsed] = useState('00:00:00');
  const [simBusy, setSimBusy] = useState(false);
  const [returnedReal, setReturnedReal] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [actBusy, setActBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  const [opsLog, setOpsLog] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLang, setPreviewLang] = useState(null);
  const [previewReload, setPreviewReload] = useState(0);
  const prevSimulatedRef = useRef(false);
  const wsRef = useRef(null);
  const isAdmin = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').role === 'admin'; } catch (e) { return false; }
  })();

  const simulate = async (profile) => {
    if (simBusy) return;
    setSimBusy(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/sessions/${sessionId}/event-state/simulate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ profile }),
      });
    } catch (e) { /* ignore */ }
    setSimBusy(false);
  };

  const fetchOpsLog = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/sessions/${sessionId}/operational-actions`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setOpsLog(await r.json());
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { fetchOpsLog(); }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!actionMsg) return;
    const t = setTimeout(() => setActionMsg(null), 6000);
    return () => clearTimeout(t);
  }, [actionMsg]);

  const runAction = async (path, body, okText) => {
    setActBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/sessions/${sessionId}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body || {}),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `Erro ${r.status}`);
      const extra = data.changed === false ? ' (sem alteração — estado já aplicado)' : '';
      setActionMsg({ ok: true, text: `${okText}${extra}` });
      fetchOpsLog();
    } catch (e) {
      setActionMsg({ ok: false, text: `Falhou: ${e.message}` });
    }
    setActBusy(false);
  };

  const ACTION_DEFS = {
    tts_suspend: (lang) => ({
      title: lang ? `Suspender TTS · ${(lang || '').toUpperCase()}` : 'Suspender TTS global',
      message: lang
        ? `O áudio traduzido em ${LANG_NAMES[lang] || lang} deixa de ser gerado. As legendas continuam ativas e a fila é preservada.`
        : 'O áudio traduzido deixa de ser gerado em TODOS os idiomas. As legendas continuam ativas e as filas são preservadas.',
      danger: !lang,
      double: !lang,
      exec: () => runAction('/actions/tts/suspend', { lang }, lang ? `TTS suspenso (${lang.toUpperCase()})` : 'TTS suspenso globalmente'),
    }),
    tts_resume: (lang) => ({
      title: lang ? `Retomar TTS · ${(lang || '').toUpperCase()}` : 'Retomar TTS global',
      message: 'O áudio traduzido volta a ser gerado. Segmentos demasiado antigos na fila são descartados para retomar perto do discurso atual.',
      danger: false,
      double: false,
      exec: () => runAction('/actions/tts/resume', { lang }, lang ? `TTS retomado (${lang.toUpperCase()})` : 'TTS retomado globalmente'),
    }),
    tts_clear_queue: (lang) => ({
      title: `Limpar fila TTS · ${(lang || '').toUpperCase()}`,
      message: `Elimina os segmentos de áudio pendentes APENAS em ${LANG_NAMES[lang] || lang} (backend + fila dos participantes). Legendas, outros idiomas e métricas históricas não são afetados.`,
      danger: true,
      double: true,
      exec: () => runAction('/actions/tts/clear-queue', { lang }, `Fila TTS limpa (${lang.toUpperCase()})`),
    }),
    resume_service: () => ({
      title: 'Retomar serviço',
      message: 'Remove todas as suspensões de TTS (globais e por idioma) e notifica os participantes de que o serviço está normalizado.',
      danger: false,
      double: false,
      exec: () => runAction('/actions/resume-service', {}, 'Serviço retomado'),
    }),
    clear_subtitles: () => ({
      title: 'Limpar legendas',
      message: 'Apaga as legendas visíveis no ecrã de TODOS os participantes, em todos os idiomas. As transcrições guardadas não são afetadas.',
      danger: true,
      double: true,
      exec: () => runAction('/clear-subtitles', {}, 'Legendas limpas para todos os participantes'),
    }),
  };

  const handleAction = (action, lang) => {
    setConfirm(ACTION_DEFS[action](lang));
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setAuthError('Sessão expirada. Faça login novamente.');
      return;
    }
    fetch(`${API}/sessions/${sessionId}/event-state`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.json();
      })
      .then(setSnap)
      .catch((e) => {
        if (e.message === '401' || e.message === '403') setAuthError('Sem autorização para esta sessão.');
      });
  }, [sessionId]);

  useEffect(() => {
    let closed = false;
    let retry = null;
    const connect = () => {
      const token = localStorage.getItem('token');
      if (!token || closed) return;
      setWsStatus('connecting');
      const ws = new WebSocket(`${WS_URL}/api/ws/event/${sessionId}`);
      wsRef.current = ws;
      ws.onopen = () => ws.send(JSON.stringify({ type: 'auth', token }));
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === 'auth_ok') { setWsStatus('connected'); return; }
          if (msg.type === 'event_state') setSnap(msg);
        } catch (err) { /* ignore */ }
      };
      ws.onclose = (ev) => {
        setWsStatus('disconnected');
        if (ev.code === 4001) { setAuthError('Sem autorização para esta sessão.'); return; }
        if (!closed) retry = setTimeout(connect, 3000);
      };
      ws.onerror = () => {};
    };
    connect();
    return () => {
      closed = true;
      if (retry) clearTimeout(retry);
      try { if (wsRef.current) wsRef.current.close(); } catch (e) { /* ignore */ }
    };
  }, [sessionId]);

  useEffect(() => {
    if (!snap) return;
    const was = prevSimulatedRef.current;
    prevSimulatedRef.current = !!snap.simulated;
    if (was && !snap.simulated) {
      setReturnedReal(true);
      const t = setTimeout(() => setReturnedReal(false), 6000);
      return () => clearTimeout(t);
    }
  }, [snap]);

  useEffect(() => {
    const iv = setInterval(() => {
      if (snap && snap.started_at) {
        const s = Math.max(0, Math.floor(Date.now() / 1000 - snap.started_at));
        const h = String(Math.floor(s / 3600)).padStart(2, '0');
        const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
        const sec = String(s % 60).padStart(2, '0');
        setElapsed(`${h}:${m}:${sec}`);
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [snap]);

  if (authError) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
        <div className="text-center space-y-4" data-testid="event-mode-auth-error">
          <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto" />
          <p className="text-slate-300 font-['Inter']">{authError}</p>
          <a href="/login" className="text-blue-400 underline text-sm">Ir para o login</a>
        </div>
      </div>
    );
  }

  if (!snap) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center" data-testid="event-mode-loading">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  const langs = snap.langs || {};
  const metrics = snap.metrics || {};
  const audio = snap.audio || {};
  const stt = snap.stt || {};
  const session = snap.session || {};
  const tel = snap.telemetry || {};
  const telAge = tel.last_update_ago;
  const dataSource = wsStatus !== 'connected'
    ? { key: 'lost', label: 'LIGAÇÃO PERDIDA', cls: 'border-red-500/50 bg-red-500/15 text-red-300 animate-pulse', Icon: WifiOff }
    : snap.simulated
      ? { key: 'sim', label: 'DADOS SIMULADOS', cls: 'border-purple-500/50 bg-purple-500/15 text-purple-300', Icon: FlaskConical }
      : (telAge === null || telAge === undefined || telAge > (tel.stale_after_s || 60))
        ? { key: 'none', label: 'SEM DADOS', cls: 'border-slate-600/50 bg-slate-600/15 text-slate-400', Icon: AlertTriangle }
        : { key: 'real', label: 'DADOS REAIS', cls: 'border-emerald-500/50 bg-emerald-500/15 text-emerald-300', Icon: Radio };
  const DSIcon = dataSource.Icon;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-['Inter']" data-testid="event-mode-page">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-950/90 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap items-center gap-3">
          <a href="/host" className="text-slate-500 hover:text-slate-300" data-testid="event-mode-back-link">
            <ArrowLeft className="w-4 h-4" />
          </a>
          <Activity className="w-5 h-5 text-blue-400" />
          <div>
            <div className="font-['Manrope'] font-bold text-white text-sm leading-tight">
              EVENT MODE <span className="text-slate-500 font-normal">· observação + controlo</span>
            </div>
            <div className="text-[11px] text-slate-500" data-testid="event-mode-session-info">
              {session.name || 'Sessão'} ({session.code}) · origem {(session.source_lang || '').toUpperCase()}
            </div>
          </div>
          <div className="flex-1" />
          <span className={`inline-flex items-center gap-1.5 rounded-full border text-[10px] font-bold px-3 py-1 tracking-wider ${dataSource.cls}`} data-testid="data-source-badge" data-source={dataSource.key}>
            <DSIcon className="w-3 h-3" />
            {dataSource.label}
            {dataSource.key === 'sim' && snap.sim_profile && (
              <span className="font-normal normal-case opacity-80" data-testid="sim-profile-label">
                · {({ normal: 'normal', degraded: 'degradado', critical: 'crítico' }[snap.sim_profile] || snap.sim_profile)}
              </span>
            )}
            {dataSource.key === 'real' && (
              <span className="font-normal normal-case opacity-80" data-testid="telemetry-age">
                · atualização há {Math.round(telAge)}s
              </span>
            )}
          </span>
          <StateBadge state={snap.overall} testId="overall-state-badge" />
          <span className="inline-flex items-center gap-1.5 text-xs text-slate-400" data-testid="provider-chip">
            <Server className="w-3.5 h-3.5" />
            {snap.provider === 'azure' ? 'Azure' : 'OpenAI'}
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs text-slate-400" data-testid="elapsed-timer">
            <Clock className="w-3.5 h-3.5" /> {elapsed}
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs" data-testid="ws-status">
            {wsStatus === 'connected'
              ? <><Wifi className="w-3.5 h-3.5 text-emerald-400" /><span className="text-emerald-400">ao vivo</span></>
              : <><WifiOff className="w-3.5 h-3.5 text-red-400" /><span className="text-red-400">{wsStatus === 'connecting' ? 'a ligar…' : 'ligação perdida'}</span></>}
          </span>
          <button
            onClick={() => setPreviewOpen((o) => !o)}
            data-testid="participant-preview-toggle"
            className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md border transition-colors ${previewOpen ? 'border-cyan-500/60 bg-cyan-500/15 text-cyan-300' : 'border-slate-700 text-slate-400 hover:text-white hover:bg-slate-800'}`}
            title="Preview da vista de participante"
          >
            <Smartphone className="w-3.5 h-3.5" /> Preview
          </button>
        </div>
      </header>

      {previewOpen && (() => {
        const previewLangs = Object.keys(langs);
        const activeLang = previewLang || previewLangs[0] || '';
        return (
          <div className="fixed right-4 top-20 z-40 w-[300px]" data-testid="participant-preview-panel">
            <div className="rounded-2xl border border-slate-700 bg-slate-900/95 backdrop-blur p-3 shadow-2xl space-y-2.5">
              <div className="flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-cyan-400" />
                <span className="text-xs font-['Manrope'] font-bold text-white">Preview do Participante</span>
                <button onClick={() => setPreviewReload((k) => k + 1)} className="ml-auto p-1 text-slate-400 hover:text-white transition-colors" title="Recarregar preview" data-testid="preview-reload-btn">
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => setPreviewOpen(false)} className="p-1 text-slate-400 hover:text-white transition-colors" data-testid="preview-close-btn">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              {previewLangs.length > 1 && (
                <div className="flex flex-wrap gap-1">
                  {previewLangs.map((l) => (
                    <button
                      key={l}
                      onClick={() => setPreviewLang(l)}
                      data-testid={`preview-lang-${l}`}
                      className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border transition-colors ${activeLang === l ? 'bg-cyan-500/20 border-cyan-500/60 text-cyan-300' : 'border-slate-700 text-slate-400 hover:text-white'}`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              )}
              <div className="mx-auto w-[264px] rounded-[2rem] border-[6px] border-slate-950 bg-slate-950 overflow-hidden ring-1 ring-slate-700">
                <div className="h-5 bg-slate-950 flex items-center justify-center">
                  <div className="w-16 h-1.5 rounded-full bg-slate-800" />
                </div>
                {session.code && activeLang ? (
                  <iframe
                    key={`${activeLang}-${previewReload}`}
                    src={`${window.location.origin}/join/${session.code}?monitor=1&lang=${activeLang}`}
                    title="Preview do participante"
                    className="w-full h-[520px] bg-slate-950"
                    data-testid="preview-iframe"
                  />
                ) : (
                  <div className="w-full h-[520px] flex items-center justify-center text-xs text-slate-500 px-4 text-center">
                    Sem idiomas ativos para pré-visualizar.
                  </div>
                )}
              </div>
              <p className="text-[9px] text-slate-500 leading-relaxed">
                Ligação de monitor: não conta nos participantes nem nas métricas ACK. Áudio desligado por defeito (anti-eco) — pode ativá-lo dentro do preview.
              </p>
            </div>
          </div>
        );
      })()}

      <main className="max-w-7xl mx-auto px-4 py-5 space-y-5">
        {returnedReal && (
          <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-xs px-4 py-2 flex items-center gap-2" data-testid="real-data-restored-banner">
            <Wifi className="w-3.5 h-3.5" /> Simulação terminada — o Event Mode voltou aos dados reais.
          </div>
        )}

        {actionMsg && (
          <div className={`rounded-lg border text-xs px-4 py-2 flex items-center gap-2 ${actionMsg.ok ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' : 'border-red-500/40 bg-red-500/10 text-red-300'}`} data-testid="action-feedback">
            {actionMsg.ok ? <PlayCircle className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
            {actionMsg.text}
          </div>
        )}

        {snap.tts_suspended_global && (
          <div className="rounded-lg border border-orange-500/40 bg-orange-500/10 text-orange-300 text-xs px-4 py-2 flex items-center gap-2" data-testid="tts-global-suspended-banner">
            <VolumeX className="w-3.5 h-3.5" /> TTS suspenso globalmente — as legendas continuam a ser entregues a todos os participantes.
          </div>
        )}

        {/* Bloco B: Operational controls */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/70 px-4 py-3 flex flex-wrap items-center gap-2" data-testid="controls-panel">
          <ShieldAlert className="w-4 h-4 text-blue-400 shrink-0" />
          <span className="text-xs text-slate-300 font-semibold mr-1">Ações de controlo:</span>
          {snap.tts_suspended_global ? (
            <button onClick={() => handleAction('tts_resume', null)} disabled={actBusy} data-testid="ctrl-resume-global-btn"
              className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md border border-emerald-600/50 text-emerald-300 hover:bg-emerald-600/15 disabled:opacity-40">
              <Volume2 className="w-3 h-3" /> Retomar TTS global
            </button>
          ) : (
            <button onClick={() => handleAction('tts_suspend', null)} disabled={actBusy} data-testid="ctrl-suspend-global-btn"
              className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md border border-orange-600/50 text-orange-300 hover:bg-orange-600/15 disabled:opacity-40">
              <VolumeX className="w-3 h-3" /> Suspender TTS global
            </button>
          )}
          <button onClick={() => handleAction('resume_service')} disabled={actBusy} data-testid="ctrl-resume-service-btn"
            className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md border border-blue-600/50 text-blue-300 hover:bg-blue-600/15 disabled:opacity-40">
            <PlayCircle className="w-3 h-3" /> Retomar serviço
          </button>
          <button onClick={() => handleAction('clear_subtitles')} disabled={actBusy} data-testid="ctrl-clear-subtitles-btn"
            className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md border border-red-600/50 text-red-300 hover:bg-red-600/15 disabled:opacity-40">
            <Eraser className="w-3 h-3" /> Limpar legendas
          </button>
          <span className="text-[10px] text-slate-500 ml-auto">
            Ações destrutivas exigem dupla confirmação · tudo auditado
          </span>
        </div>

        {isAdmin && (
          <div className="rounded-xl border border-purple-500/30 bg-purple-500/5 px-4 py-3 flex flex-wrap items-center gap-2" data-testid="sim-panel">
            <FlaskConical className="w-4 h-4 text-purple-400 shrink-0" />
            <span className="text-xs text-purple-300 font-semibold mr-1">Simulação (demo · só apresentação):</span>
            <button onClick={() => simulate('normal')} disabled={simBusy} data-testid="sim-normal-btn"
              className="text-[11px] px-2.5 py-1 rounded-md border border-emerald-600/50 text-emerald-300 hover:bg-emerald-600/15 disabled:opacity-40">
              Normal
            </button>
            <button onClick={() => simulate('degraded')} disabled={simBusy} data-testid="sim-degraded-btn"
              className="text-[11px] px-2.5 py-1 rounded-md border border-amber-600/50 text-amber-300 hover:bg-amber-600/15 disabled:opacity-40">
              Degradado
            </button>
            <button onClick={() => simulate('critical')} disabled={simBusy} data-testid="sim-critical-btn"
              className="text-[11px] px-2.5 py-1 rounded-md border border-red-600/50 text-red-300 hover:bg-red-600/15 disabled:opacity-40">
              Crítico
            </button>
            <button onClick={() => simulate('progressive')} disabled={simBusy} data-testid="sim-progressive-btn"
              className="text-[11px] px-2.5 py-1 rounded-md border border-blue-600/50 text-blue-300 hover:bg-blue-600/15 disabled:opacity-40">
              Cenário progressivo
            </button>
            {snap.simulated && (
              <button onClick={() => simulate('reset')} disabled={simBusy} data-testid="sim-stop-btn"
                className="text-[11px] px-2.5 py-1 rounded-md bg-purple-600/30 border border-purple-500/60 text-purple-200 hover:bg-purple-600/50 disabled:opacity-40 font-semibold">
                ✕ Terminar simulação
              </button>
            )}
            <span className="text-[10px] text-slate-500 ml-auto">
              Não afeta o pipeline real · expira em 5 min · registada em auditoria
            </span>
          </div>
        )}

        {/* Top row: audio + stt + global metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Audio source */}
          <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4 space-y-3" data-testid="audio-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-['Manrope'] font-bold text-white">
                {audio.source === 'rtmp' ? <Radio className="w-4 h-4 text-blue-400" /> : <Mic className="w-4 h-4 text-blue-400" />}
                Fonte de Áudio ({(audio.source || 'mic').toUpperCase()})
              </div>
              <StateBadge state={audio.status} small testId="audio-state-badge" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Metric label="Último chunk" value={fmtAgo(audio.gap_s)} testId="audio-gap" />
              <Metric label="Chunks" value={String(audio.chunks || 0)} testId="audio-chunks" />
              <Metric label="Nível" value={audio.level !== null && audio.level !== undefined ? `${audio.level}%` : '—'} testId="audio-level" />
            </div>
            <div className="text-[10px] text-slate-500 flex flex-wrap gap-x-3">
              <span>Host {snap.host_connected ? 'ligado' : 'desligado'}</span>
              {audio.cause && <span className="text-slate-400" data-testid="audio-cause">Causa: {audio.cause}</span>}
              {audio.stream_connected !== undefined && (
                <span data-testid="audio-stream-flag">Stream {audio.stream_connected ? 'ativo' : 'sem sinal'}</span>
              )}
              {audio.voice_active !== undefined && (
                <span data-testid="audio-voice-flag">Voz {audio.voice_active ? 'ativa' : 'inativa'}</span>
              )}
            </div>
          </div>

          {/* STT */}
          <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4 space-y-3" data-testid="stt-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-['Manrope'] font-bold text-white">
                <MessageSquareText className="w-4 h-4 text-blue-400" />
                STT (transcrição)
              </div>
              <StateBadge state={stt.status} small testId="stt-state-badge" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Metric label="Lat. média 60s" value={fmtS(stt.avg_60s)} sub={`p95 ${fmtS(stt.p95_60s)}`} trend={stt.trend} testId="stt-latency" />
              <Metric label="Segmentos 60s" value={String(stt.count_60s || 0)} testId="stt-count" />
              <Metric label="Erros 5min" value={String(stt.errors_5m || 0)} testId="stt-errors" />
            </div>
            <div className="text-[10px] text-slate-500">Último OK: {fmtAgo(stt.last_ok_ago)}</div>
          </div>

          {/* Global metrics */}
          <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4 space-y-3" data-testid="global-metrics-card">
            <div className="flex items-center gap-2 text-sm font-['Manrope'] font-bold text-white">
              <Users className="w-4 h-4 text-blue-400" />
              Métricas Globais
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Metric
                label="Lat. legendas"
                value={fmtS((metrics.subtitle_latency || {}).p50)}
                sub={`p95 ${fmtS((metrics.subtitle_latency || {}).p95)}`}
                trend={(metrics.subtitle_latency || {}).trend}
                testId="global-subtitle-latency"
              />
              <Metric
                label="Lat. áudio"
                value={fmtS((metrics.audio_latency || {}).p50)}
                sub={`p95 ${fmtS((metrics.audio_latency || {}).p95)}`}
                trend={(metrics.audio_latency || {}).trend}
                testId="global-audio-latency"
              />
              <Metric label="Participantes" value={String(metrics.participants_total || 0)} testId="global-participants" />
            </div>
            <div className="text-[10px] text-slate-500">
              Erros de pipeline (5min): {metrics.pipeline_errors_5m || 0}
            </div>
            {snap.acks && snap.acks.sampling && snap.acks.sampling.connected > 0 && (
              <div className="text-[10px] text-slate-500" data-testid="ack-sample-distribution">
                Amostra ACK: {snap.acks.sampling.sample_size}/{snap.acks.sampling.connected}
                {snap.acks.sampling.active ? ' (amostragem rotativa ativa)' : ' (ACK completo)'}
                {Object.keys(snap.acks.langs || {}).length > 0 && (
                  <> · {Object.entries(snap.acks.langs).map(([l, a]) => `${l.toUpperCase()} ${a.sample_size}/${a.eligible}`).join(' · ')}</>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Per language */}
        <div>
          <h2 className="text-xs uppercase tracking-widest text-slate-500 mb-3 font-semibold">Por idioma</h2>
          {Object.keys(langs).length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-800 p-8 text-center text-slate-500 text-sm" data-testid="no-langs">
              Sem idiomas ativos — inicie a sessão para ver o estado do pipeline.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="lang-cards-grid">
              {Object.entries(langs).map(([lang, data]) => (
                <LangCard
                  key={lang}
                  lang={lang}
                  data={data}
                  acks={((snap.acks || {}).langs || {})[lang]}
                  samplingActive={!!((snap.acks || {}).sampling || {}).active}
                  onAction={handleAction}
                  busy={actBusy}
                />
              ))}
            </div>
          )}
        </div>

        {/* Recent errors */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4" data-testid="errors-card">
          <div className="flex items-center gap-2 text-sm font-['Manrope'] font-bold text-white mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            Erros Recentes
            <span className="text-[10px] text-slate-500 font-normal">(últimos 20)</span>
          </div>
          {(snap.recent_errors || []).length === 0 ? (
            <div className="text-xs text-slate-500" data-testid="no-errors">Sem erros registados.</div>
          ) : (
            <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="errors-list">
              {snap.recent_errors.map((e, i) => (
                <div key={i} className="flex items-start gap-2 text-xs" data-testid={`error-row-${i}`}>
                  <span className="text-slate-600 font-mono shrink-0">{(e.ts || '').substring(11, 19)}</span>
                  <span className={`shrink-0 uppercase text-[9px] font-bold px-1.5 py-0.5 rounded ${e.severity === 'warning' ? 'bg-amber-500/15 text-amber-400' : 'bg-red-500/15 text-red-400'}`}>
                    {e.stage}{e.lang ? `/${e.lang}` : ''}
                  </span>
                  <span className="text-slate-400 break-all">{e.message}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-[10px] text-slate-600 pb-6">
          Estados normal / degradado / crítico calculados com thresholds iniciais configuráveis — a calibrar em testes e eventos reais. Histerese de recuperação: {((snap.thresholds || {}).hysteresis_s || 30)}s. Ações operacionais (Bloco B) auditadas em base de dados.
        </p>
      </main>

      {/* Bloco B: operational actions audit trail */}
      <div className="max-w-7xl mx-auto px-4 pb-8 -mt-2">
        <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4" data-testid="actions-log-card">
          <div className="flex items-center gap-2 text-sm font-['Manrope'] font-bold text-white mb-3">
            <ListChecks className="w-4 h-4 text-blue-400" />
            Ações Operacionais
            <span className="text-[10px] text-slate-500 font-normal">(auditoria · últimas 50)</span>
          </div>
          {opsLog.length === 0 ? (
            <div className="text-xs text-slate-500" data-testid="no-ops-actions">Sem ações registadas nesta sessão.</div>
          ) : (
            <div className="space-y-1.5 max-h-48 overflow-y-auto" data-testid="actions-log-list">
              {opsLog.map((a, i) => (
                <div key={a.id || i} className="flex items-start gap-2 text-xs" data-testid={`ops-action-row-${i}`}>
                  <span className="text-slate-600 font-mono shrink-0">{(a.timestamp || '').substring(11, 19)}</span>
                  <span className="shrink-0 uppercase text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300">
                    {a.action}{a.lang ? `/${a.lang}` : ''}
                  </span>
                  <span className="text-slate-400 break-all">{a.user_email}{a.error ? ` · ERRO: ${a.error}` : ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        confirm={confirm}
        onCancel={() => setConfirm(null)}
        onConfirm={() => { const c = confirm; setConfirm(null); if (c) c.exec(); }}
      />
    </div>
  );
}
